import {
  Descendant,
  Editor,
  Element as SlateElement,
  Text,
  Transforms,
} from 'slate'

import { EditorMode } from '../constants'
import { EditorModeType } from '../types'
import type { CustomElement, FormattedText } from '../types/slate'

import { countCharacters } from './slateUtils'

export const processPaste = (
  event: ClipboardEvent,
  editor: Editor,
  maxCharacters: number,
  mode: EditorModeType
): boolean => {
  const clipboardData = event.clipboardData

  if (!clipboardData) {
    return false
  }

  // Extract clipboard data
  const htmlData = clipboardData.getData('text/html')
  const plainTextData = clipboardData.getData('text/plain')

  // If no data available, let default behavior handle it
  if (!htmlData && !plainTextData) {
    return false
  }

  // Calculate current character count
  const currentContent = editor.children
  const currentCount = countCharacters(currentContent)

  // Determine what content to insert
  let nodesToInsert: Descendant[]

  if (mode === EditorMode.PLAIN || !htmlData) {
    // Plain text mode or no HTML data: insert plain text only
    nodesToInsert = createPlainTextNodes(plainTextData || htmlData)
  } else {
    // Markdown mode with HTML data: convert to Slate nodes
    nodesToInsert = htmlToSlate(htmlData)
  }

  // Calculate character count of pasted content
  const pasteCount = countCharacters(nodesToInsert)
  const totalCount = currentCount + pasteCount

  // Check character limit and truncate if needed
  if (totalCount > maxCharacters) {
    const remainingSpace = maxCharacters - currentCount

    if (remainingSpace <= 0) {
      return true
    }

    // Truncate pasted content to fit
    nodesToInsert = truncateNodes(nodesToInsert, remainingSpace)
  }

  insertNodes(editor, nodesToInsert)

  return true
}

const htmlToSlate = (html: string): Descendant[] => {
  if (!html || html.trim() === '') {
    return []
  }

  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const body = doc.body

    if (!body) {
      return []
    }

    const nodes: Descendant[] = []

    Array.from(body.childNodes).forEach((childNode) => {
      const processedNodes = processNode(childNode)
      nodes.push(...processedNodes)
    })

    if (nodes.length === 0) {
      return [
        {
          type: 'paragraph',
          children: [{ text: '' }],
        } as CustomElement,
      ]
    }

    return nodes
  } catch {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html
    const plainText = tempDiv.textContent || tempDiv.innerText || ''
    return createPlainTextNodes(plainText)
  }
}

const processNode = (node: Node): Descendant[] => {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent || ''
    if (text.trim() === '') {
      return []
    }
    return [{ text } as FormattedText]
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as HTMLElement
    const tagName = element.tagName.toLowerCase()

    if (shouldSkipElement(tagName)) {
      return []
    }

    const children: Descendant[] = []
    Array.from(element.childNodes).forEach((childNode) => {
      const processedChildren = processNode(childNode)
      children.push(...processedChildren)
    })

    if (children.length === 0) {
      children.push({ text: '' } as FormattedText)
    }

    switch (tagName) {
      case 'p':
        return [
          {
            type: 'paragraph',
            children: normalizeChildren(children),
          } as CustomElement,
        ]

      case 'h1':
      case 'h2': {
        const level = Number.parseInt(tagName[1]) as 1 | 2
        return [
          {
            type: 'heading',
            level,
            children: normalizeChildren(children),
          } as CustomElement,
        ]
      }

      case 'ul':
        return [
          {
            type: 'bulleted-list',
            children: normalizeListChildren(children),
          } as CustomElement,
        ]

      case 'ol':
        return [
          {
            type: 'numbered-list',
            children: normalizeListChildren(children),
          } as CustomElement,
        ]

      case 'li':
        return [
          {
            type: 'list-item',
            children: normalizeChildren(children),
          } as CustomElement,
        ]

      case 'strong':
      case 'b':
        return applyTextFormat(children, 'bold')

      case 'em':
      case 'i':
        return applyTextFormat(children, 'italic')

      case 'code':
        return applyTextFormat(children, 'code')

      case 'br':
        return [{ text: '\n' } as FormattedText]

      case 'div':
      case 'span':
        return children

      default:
        return children
    }
  }

  return []
}

const shouldSkipElement = (tagName: string): boolean => {
  const unsupportedTags = [
    'style',
    'script',
    'img',
    'table',
    'thead',
    'tbody',
    'tr',
    'td',
    'th',
    'font',
    'svg',
    'iframe',
    'video',
    'audio',
    'canvas',
  ]
  return unsupportedTags.includes(tagName)
}

const normalizeChildren = (children: Descendant[]): Descendant[] => {
  const normalized: Descendant[] = []

  children.forEach((child) => {
    if (Text.isText(child)) {
      if (
        normalized.length > 0 &&
        Text.isText(normalized[normalized.length - 1])
      ) {
        const lastText = normalized[normalized.length - 1] as FormattedText
        normalized[normalized.length - 1] = {
          ...lastText,
          text: lastText.text + child.text,
        }
      } else {
        normalized.push(child)
      }
    } else if (SlateElement.isElement(child)) {
      const text = extractTextFromElement(child)
      if (text) {
        normalized.push({ text } as FormattedText)
      }
    }
  })

  if (normalized.length === 0) {
    normalized.push({ text: '' } as FormattedText)
  }

  return normalized
}

const normalizeListChildren = (children: Descendant[]): Descendant[] => {
  const listItems: CustomElement[] = []

  children.forEach((child) => {
    if (SlateElement.isElement(child) && child.type === 'list-item') {
      listItems.push(child as CustomElement)
    } else if (Text.isText(child) && child.text.trim() !== '') {
      listItems.push({
        type: 'list-item',
        children: [child],
      } as CustomElement)
    }
  })

  if (listItems.length === 0) {
    listItems.push({
      type: 'list-item',
      children: [{ text: '' }],
    } as CustomElement)
  }

  return listItems
}

const applyTextFormat = (
  children: Descendant[],
  format: 'bold' | 'italic' | 'code'
): Descendant[] => {
  return children.map((child) => {
    if (Text.isText(child)) {
      return {
        ...child,
        [format]: true,
      } as FormattedText
    }
    return child
  })
}

const extractTextFromElement = (element: CustomElement): string => {
  return element.children
    .map((child) => {
      if (Text.isText(child)) {
        return child.text
      }
      if (SlateElement.isElement(child)) {
        return extractTextFromElement(child)
      }
      return ''
    })
    .join('')
}

const createPlainTextNodes = (text: string): Descendant[] => {
  if (!text || text.trim() === '') {
    return []
  }

  return [{ text } as FormattedText]
}

const truncateTextNode = (
  node: FormattedText,
  remainingSpace: number
): FormattedText | null => {
  if (remainingSpace <= 0) {
    return null
  }
  return {
    ...node,
    text: node.text.substring(0, remainingSpace),
  } as FormattedText
}

const truncateElementNode = (
  element: CustomElement,
  remainingSpace: number
): CustomElement | null => {
  const truncatedChildren = truncateNodes(element.children, remainingSpace)
  if (truncatedChildren.length === 0) {
    return null
  }
  return {
    ...element,
    children: truncatedChildren,
  } as CustomElement
}

const processTextNodeForTruncation = (
  node: FormattedText,
  currentCount: number,
  maxCharacters: number,
  truncatedNodes: Descendant[]
): { shouldBreak: boolean; newCount: number } => {
  const textLength = node.text.length
  if (currentCount + textLength <= maxCharacters) {
    truncatedNodes.push(node)
    return { shouldBreak: false, newCount: currentCount + textLength }
  }

  const remainingSpace = maxCharacters - currentCount
  const truncated = truncateTextNode(node, remainingSpace)
  if (truncated) {
    truncatedNodes.push(truncated)
  }
  return { shouldBreak: true, newCount: currentCount }
}

const processElementNodeForTruncation = (
  element: CustomElement,
  currentCount: number,
  maxCharacters: number,
  truncatedNodes: Descendant[]
): { shouldBreak: boolean; newCount: number } => {
  const elementText = countCharacters([element])
  if (currentCount + elementText <= maxCharacters) {
    truncatedNodes.push(element)
    return { shouldBreak: false, newCount: currentCount + elementText }
  }

  const remainingSpace = maxCharacters - currentCount
  const truncated = truncateElementNode(element, remainingSpace)
  if (truncated) {
    truncatedNodes.push(truncated)
  }
  return { shouldBreak: true, newCount: currentCount }
}

export const truncateNodes = (
  nodes: Descendant[],
  maxCharacters: number
): Descendant[] => {
  let currentCount = 0
  const truncatedNodes: Descendant[] = []

  for (const node of nodes) {
    if (Text.isText(node)) {
      const result = processTextNodeForTruncation(
        node,
        currentCount,
        maxCharacters,
        truncatedNodes
      )
      currentCount = result.newCount
      if (result.shouldBreak) break
    } else if (SlateElement.isElement(node)) {
      const result = processElementNodeForTruncation(
        node,
        currentCount,
        maxCharacters,
        truncatedNodes
      )
      currentCount = result.newCount
      if (result.shouldBreak) break
    }
  }

  return truncatedNodes
}

const insertNodes = (editor: Editor, nodes: Descendant[]): void => {
  if (nodes.length === 0) {
    return
  }

  nodes.forEach((node) => {
    if (Text.isText(node)) {
      Transforms.insertText(editor, node.text)
    } else if (SlateElement.isElement(node)) {
      Transforms.insertNodes(editor, node)
    }
  })
}
